import { useTranslation } from "react-i18next";

interface GaugeProps {
  label: string;
  used: number;
  total: number;
  color: string;
  usedLabel: string;
}

function RadialGauge({ label, used, total, color, usedLabel }: GaugeProps) {
  const safeUsed = used ?? 0;
  const safeTotal = total ?? 0;
  const pct = safeTotal > 0 ? Math.min((safeUsed / safeTotal) * 100, 100) : 0;
  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (pct / 100) * circumference;

  const isOver = pct >= 100;
  const isWarning = pct > 80 && pct < 100;

  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative w-[120px] h-[120px] mb-2">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r="52" fill="none" stroke="oklch(var(--background-200))" strokeWidth="10" />
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-xl font-bold font-heading ${isOver ? "text-red-500" : isWarning ? "text-amber-600" : "text-foreground-950"}`}>
            {pct.toFixed(0)}%
          </span>
          <span className="text-[10px] text-foreground-400 mt-0.5">{usedLabel}</span>
        </div>
      </div>
      <p className="text-xs text-foreground-600 font-medium">{label}</p>
      <p className="text-[10px] text-foreground-400">
        {safeUsed.toLocaleString()} / {safeTotal.toLocaleString()}
      </p>
    </div>
  );
}

interface PlanUsageRadialProps {
  plan: {
    features: {
      messages: number;
      tokens: number;
      input: number;
      output: number;
    };
  };
  usage: {
    messages: number;
    tokens: number;
    input: number;
    output: number;
  };
}

export default function PlanUsageRadial({ plan, usage }: PlanUsageRadialProps) {
  const { t } = useTranslation("dashboard");
  const features = plan?.features ?? { messages: 0, tokens: 0, input: 0, output: 0 };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
      <h3 className="font-heading text-lg text-foreground-950 mb-1">{t("charts.planCapacity")}</h3>
      <p className="text-xs text-foreground-500 mb-5">{t("charts.usageVsLimits")}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <RadialGauge
          label={t("charts.messages")}
          used={usage?.messages ?? 0}
          total={features.messages}
          color="oklch(var(--accent-500))"
          usedLabel={t("charts.used")}
        />
        <RadialGauge
          label={t("charts.tokens")}
          used={usage?.tokens ?? 0}
          total={features.tokens}
          color="oklch(var(--primary-500))"
          usedLabel={t("charts.used")}
        />
        <RadialGauge
          label={t("charts.input")}
          used={usage?.input ?? 0}
          total={features.input}
          color="oklch(var(--secondary-500))"
          usedLabel={t("charts.used")}
        />
        <RadialGauge
          label={t("charts.output")}
          used={usage?.output ?? 0}
          total={features.output}
          color="#10b981"
          usedLabel={t("charts.used")}
        />
      </div>
    </div>
  );
}